// @ts-check

import { html } from "lit-html";
import {
  renderDetailRow,
  renderErrorMessage,
  renderInputField,
  renderPlaceholder,
  renderSectionCard,
} from "../ui.js";
import { formatEnglishNumber, hasText, parseNumber } from "../utils.js";

/** @typedef {ReturnType<typeof html>} LitView */
/** @typedef {import("./index.js").ToolEnv} ToolEnv */
/** @typedef {ReturnType<typeof createCrossMultiplicationState>} CrossMultiplicationState */
/** @typedef {{ kind: "empty" } | { kind: "error", error: string } | { kind: "ready", resultText: string, formula: string, calculation: string }} CrossResult */

export const crossMultiplicationTool = {
  key: "cross",
  path: "/cross-multiplication",
  title: "Cross Multiplication",
  blurb: "Direct and inverse proportion formulas with one fast result box.",
  badge: "Math",
  accentClass: "bg-gradient-to-r from-sky-300 via-cyan-300 to-blue-300",
  createState: createCrossMultiplicationState,
  render: renderCrossMultiplicationPage,
};

function createCrossMultiplicationState() {
  return {
    proportion: "direct",
    valueA: "",
    valueB: "",
    valueC: "",
  };
}

/**
 * @param {CrossMultiplicationState} toolState
 * @param {ToolEnv} env
 * @returns {LitView}
 */
function renderCrossMultiplicationPage(toolState, env) {
  const result = computeCrossMultiplicationResult(
    toolState.proportion,
    toolState.valueA,
    toolState.valueB,
    toolState.valueC,
  );
  const isDirect = toolState.proportion === "direct";
  const resultText = result.kind === "ready" ? result.resultText : "─";
  const arrowTop = isDirect ? "→" : "╲";
  const arrowBottom = isDirect ? "→" : "╱";

  return html`
    <section class="grid gap-4 xl:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] xl:items-start">
      ${renderSectionCard(
        "Cross multiplication",
        "Switch between direct and inverse proportion and the result updates instantly.",
        html`
          <div class="flex flex-wrap gap-3">
            <label class="inline-flex items-center gap-2 rounded-lg border border-stone-300 bg-white px-4 py-2 text-sm text-stone-700">
              <input
                class="h-4 w-4 border-stone-400 bg-white text-stone-900 focus:ring-stone-400/30"
                type="radio"
                name="cross-proportion"
                value="direct"
                ?checked=${isDirect}
                @input=${function () {
                  toolState.proportion = "direct";
                  env.scheduleRender();
                }}
              />
              Direct proportion
            </label>
            <label class="inline-flex items-center gap-2 rounded-lg border border-stone-300 bg-white px-4 py-2 text-sm text-stone-700">
              <input
                class="h-4 w-4 border-stone-400 bg-white text-stone-900 focus:ring-stone-400/30"
                type="radio"
                name="cross-proportion"
                value="inverse"
                ?checked=${!isDirect}
                @input=${function () {
                  toolState.proportion = "inverse";
                  env.scheduleRender();
                }}
              />
              Inverse proportion
            </label>
          </div>

          <div class="grid grid-cols-[1fr_auto_1fr] items-end gap-x-3 gap-y-4">
            ${renderInputField({
              label: "A",
              value: toolState.valueA,
              placeholder: "3",
              onInput: function (event) {
                env.handleFieldInput(toolState, "valueA", event);
              },
            })}
            <div class="pb-3 text-center text-lg text-stone-400 select-none">${arrowTop}</div>
            ${renderInputField({
              label: "B",
              value: toolState.valueB,
              placeholder: "6",
              onInput: function (event) {
                env.handleFieldInput(toolState, "valueB", event);
              },
            })}

            ${renderInputField({
              label: "C",
              value: toolState.valueC,
              placeholder: "5",
              onInput: function (event) {
                env.handleFieldInput(toolState, "valueC", event);
              },
            })}
            <div class="pb-3 text-center text-lg text-stone-400 select-none">${arrowBottom}</div>
            <div class="grid gap-1.5">
              <span class="text-sm font-medium text-stone-700">X</span>
              <div class="flex h-11 items-center rounded-lg bg-stone-100 px-4 text-xl font-semibold tracking-tight text-stone-900">
                ${resultText}
              </div>
            </div>
          </div>

          <div>${renderCrossResult(result)}</div>
        `,
      )}

      ${renderSectionCard(
        "How it works",
        "The formulas stay visible so you can validate the result instead of trusting a black box.",
        html`
          <div class="grid gap-4">
            <div class="rounded-lg bg-stone-100 p-4">
              <h3 class="text-base font-semibold text-stone-900">Direct proportion</h3>
              <p class="mt-2 text-sm leading-6 text-stone-500">Quantities grow or shrink together.</p>
              <p class="mt-3 rounded-md border border-stone-200 bg-white px-4 py-3 font-mono text-sm text-stone-700">
                A / B = C / X → X = (B × C) / A
              </p>
            </div>

            <div class="rounded-lg bg-stone-100 p-4">
              <h3 class="text-base font-semibold text-stone-900">Inverse proportion</h3>
              <p class="mt-2 text-sm leading-6 text-stone-500">One quantity grows while the other shrinks.</p>
              <p class="mt-3 rounded-md border border-stone-200 bg-white px-4 py-3 font-mono text-sm text-stone-700">
                A × B = C × X → X = (A × B) / C
              </p>
            </div>
          </div>
        `,
      )}
    </section>
  `;
}

/**
 * @param {CrossResult} result
 * @returns {LitView}
 */
function renderCrossResult(result) {
  if (result.kind === "empty") {
    return renderPlaceholder("Enter A, B, and C to calculate X.");
  }

  if (result.kind === "error") {
    return renderErrorMessage(result.error);
  }

  return html`
    <div class="rounded-lg bg-stone-100 p-4">
      <div class="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-[minmax(0,1fr)_auto]">
        ${renderDetailRow("Result", result.resultText)}
        ${renderDetailRow("Formula", result.formula)}
        ${renderDetailRow("Calculation", result.calculation)}
      </div>
    </div>
  `;
}

/**
 * @param {string} proportion
 * @param {string} aText
 * @param {string} bText
 * @param {string} cText
 * @returns {CrossResult}
 */
function computeCrossMultiplicationResult(proportion, aText, bText, cText) {
  if (!hasText(aText) || !hasText(bText) || !hasText(cText)) {
    return { kind: "empty" };
  }

  const a = parseNumber(aText);
  const b = parseNumber(bText);
  const c = parseNumber(cText);
  if (!Number.isFinite(a) || !Number.isFinite(b) || !Number.isFinite(c)) {
    return { kind: "error", error: "Please enter valid numbers for A, B, and C." };
  }

  let resultValue = 0;
  let formula = "";
  let calculation = "";
  if (proportion === "direct") {
    if (a === 0) {
      return { kind: "error", error: "A cannot be zero for direct proportion." };
    }

    resultValue = (b * c) / a;
    formula = "A / B = C / X";
    calculation = `X = (${formatEnglishNumber(b, 4)} × ${formatEnglishNumber(c, 4)}) / ${formatEnglishNumber(a, 4)}`;
  } else {
    if (c === 0) {
      return { kind: "error", error: "C cannot be zero for inverse proportion." };
    }

    resultValue = (a * b) / c;
    formula = "A × B = C × X";
    calculation = `X = (${formatEnglishNumber(a, 4)} × ${formatEnglishNumber(b, 4)}) / ${formatEnglishNumber(c, 4)}`;
  }

  return {
    kind: "ready",
    resultText: formatEnglishNumber(resultValue, 4),
    formula,
    calculation,
  };
}
